import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { BULK_ORDER_IMPORT_MAX, BULK_REVENUE_MAX, BULK_TOO_MANY, DATE_BASES, ORDER_BULK_MAX, ORDER_SOURCES, QUICK_KEYS } from '../order-board.ts'
import { BoolQuery, ISODate, ISOTimestamp, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — ĐƠN HÀNG RT (F10) ══════════════════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/orders`.
 *
 * ⚠ `sellPrice` KHÔNG có trong đầu vào: nó là số DẪN XUẤT từ `revenues`, máy chủ luôn tính lại
 *   (`azSales.ts:24`). Nhận nó từ màn hình là mở đường cho một đơn có tổng tiền khác tổng các dòng.
 *
 * ⚠ `orderStatus` KHÔNG có trong bản vá sửa đơn — đổi trạng thái đi bằng cửa riêng `/status`, nơi
 *   luật "ai được đổi" và "đơn đã giao thì khoá" được áp.
 */

export const OrderStatus = z.enum([
  'pending', 'confirmed', 'picking', 'awaiting_transport',
  'transiting', 'awaiting_delivery', 'delivered', 'cancelled',
])
export type OrderStatus = z.infer<typeof OrderStatus>

/**
 * Một khoản CHI PHÍ ĐƠN HÀNG trên form đơn (bản cũ `AzJobFeeDraft`) — bốn nhãn cố định, số ÂM được (khuyến
 * mãi / động lực cho sale, user 17/08). Đối tượng phải trả bỏ trống = khách của đơn.
 */
export const JobFee = z.object({
  label: z.enum(['Hoa hồng', 'Chi phí 1', 'Chi phí 2', 'Chi phí 3']),
  // Nhận cả số lẫn chuỗi như dòng doanh thu; máy chủ chuẩn hoá về chuỗi tiền trước khi lưu.
  amount: z.union([z.number(), z.string()]),
  payeeKind: z.enum(['customer', 'vendor', 'staff']).optional(),
  payeeId: blankAsAbsent(Uuid),
  payeeName: z.string().optional(),
  note: z.string().optional(),
})
export type JobFeeT = z.infer<typeof JobFee>

/** Một dòng doanh thu. Khuôn LỎNG có chủ ý — bản cũ cho sale gõ nhãn và tuyến tự do. */
const RevenueLine = z.object({
  kind: z.enum(['freight', 'other', 'surcharge', 'onbehalf']),
  label: z.string().optional(),
  // Tiền của DÒNG: nhận cả số lẫn chuỗi vì bản cũ lưu `number` trong JSON; miền tự chuẩn hoá.
  amount: z.union([z.number(), z.string()]),
  // Ô thuế suất là ô FORM — để trống gửi `''`, mà `.optional()` trần từ chối chuỗi rỗng.
  vatRate: blankAsAbsent(z.coerce.number()),
  /** Diễn giải dòng cước do MÁY viết (đổi điểm lấy/giao thì viết lại); `false` = người đã gõ tay. */
  labelAuto: z.boolean().optional(),
  fromPlace: z.string().optional(),
  toPlace: z.string().optional(),
  unit: z.string().optional(),
  leadTime: z.string().optional(),
})

const orderFields = {
  quoteId: blankAsAbsent(Uuid),
  kind: z.enum(['outbound', 'inbound', 'internal']).optional(),
  pickupAddr: z.string().optional(),
  dropAddr: z.string().optional(),
  pickups: z.array(z.unknown()).optional(),
  drops: z.array(z.unknown()).optional(),
  cargo: z.string().optional(),
  weightTon: blankAsAbsent(z.coerce.number()),
  contNo: z.string().optional(),
  contSize: z.enum(['20', '40', '45']).optional(),
  contType: z.string().optional(),
  packId: blankAsAbsent(Uuid),
  routeName: z.string().optional(),
  dueAt: blankAsAbsent(ISODate),
  poNo: z.string().optional(),
  bookingNo: z.string().optional(),
  billNo: z.string().optional(),
  sealNo: z.string().optional(),
  trainNo: z.string().optional(),
  currentLocation: z.string().optional(),
  needInvoice: z.boolean().optional(),
  revenues: z.array(RevenueLine).optional(),
  listPriceId: blankAsAbsent(Uuid),
  listPriceAt: blankAsAbsent(moneyField('Giá công bố')),
  costPriceAt: blankAsAbsent(moneyField('Giá vốn')),
  revenueMode: z.enum(['contract', 'negotiated']).optional(),
  contractPriceId: blankAsAbsent(Uuid),
  estOtherCostVat: blankAsAbsent(moneyField('Chi phí khác có hóa đơn')),
  estOtherCostNoVat: blankAsAbsent(moneyField('Chi phí khác không hóa đơn')),
  estOffBook: blankAsAbsent(moneyField('Chi ngoài')),
  estDepreciation: blankAsAbsent(moneyField('Phân bổ khấu hao')),
  fuelUsedL: blankAsAbsent(z.coerce.number()),
  jobFees: z.array(JobFee).max(4).optional(),
  plannedVendorId: blankAsAbsent(Uuid),
  draft: z.boolean().optional(),
  salesNote: z.string().optional(),
  note: z.string().optional(),
}

export const CreateOrderBody = z.object({
  customerId: Uuid,
  /** Ngày chạy — BẮT BUỘC, cột `NOT NULL` và là khoá sắp xếp của cả danh sách. */
  planDate: ISODate,
  /**
   * ⚠ CHỈ "Chờ xác nhận" (C3/V3-02, soi 16/10). Ô này từng nhận mọi trạng thái: sale tạo thẳng đơn
   *   "Đã giao hàng" không chặng rồi xin xuất hoá đơn. Trạng thái khác đi bằng cửa đổi trạng thái
   *   (`order:setStatus`, đòi có chặng). KHÔNG bỏ hẳn ô: zod lặng lẽ cắt khoá lạ, lời gọi gửi
   *   `delivered` sẽ nhận 201 "pending" mà không biết — trả 400 thì người gọi biết mình sai.
   */
  orderStatus: z.literal('pending').optional(),
  ...orderFields,
})

/** 📥 Nhập đơn hàng loạt từ Excel (`BulkOrderImport`) — `dryRun` soi lỗi từng dòng ở máy chủ, không ghi. */
export const BulkCreateOrderBody = z.object({ rows: z.array(CreateOrderBody).min(1, 'Chưa có dòng nào.').max(BULK_ORDER_IMPORT_MAX), dryRun: z.boolean() })
export type BulkCreateOrderBodyT = z.infer<typeof BulkCreateOrderBody>

/** 📥 Sửa doanh thu cước hàng loạt (`BulkRevenueImport`). */
export const BulkRevenueBody = z.object({
  rows: z.array(z.object({ ref: z.string().min(1), amount: moneyField('Doanh thu cước') })).min(1, 'Chưa có dòng nào.').max(BULK_REVENUE_MAX),
  dryRun: z.boolean(),
})
export type BulkRevenueBodyT = z.infer<typeof BulkRevenueBody>

/**
 * ⭐ TẠO ĐƠN + CHỐT BÁO GIÁ trong MỘT cửa (rà 16/10 C2). Bản cũ "📦 Chốt đơn" CHỈ MỞ form đơn nạp sẵn từ báo giá
 *   (`quoteToForm`, `Az.tsx:3336-3340`, `:3595-3611`); bấm Lưu ở form đó mới tạo đơn và đặt báo giá 'won'
 *   (`Az.tsx:6778-6783`), đóng form thì không sinh gì. Nên thân lệnh là ĐỦ một đơn như `CreateOrderBody` (sale đã
 *   soát ngày chạy / điểm lấy-giao / cont / chi phí) kèm báo giá + `version` của báo giá.
 */
export const OrderFromQuoteBody = CreateOrderBody.extend({
  quoteId: Uuid,
  /** `version` của BÁO GIÁ — bước "khách chốt" cũng là một lần ghi lên báo giá. */
  version: Version,
})
export type OrderFromQuoteBodyT = z.infer<typeof OrderFromQuoteBody>

export const UpdateOrderBody = z.object({
  version: Version,
  customerId: blankAsAbsent(Uuid),
  planDate: blankAsAbsent(ISODate),
  ...orderFields,
  /**
   * Đối tác dự kiến khi SỬA: `''` = XOÁ đối tác (sale để trống ô — bản cũ cho xoá, `Az.tsx:6752`); vắng = giữ nguyên.
   * Đổi/xoá thì máy chủ xoá luôn cờ "NCC đã xác nhận" (rà 16/10 T7). Tạo mới vẫn `blankAsAbsent` như các ô khác.
   */
  plannedVendorId: z.union([Uuid, z.literal('')]).optional(),
  /*
   * ⭐ Ô XOÁ ĐƯỢC KHI SỬA — `null` = XOÁ, vắng/`''` = giữ nguyên (gốc lệch "blankAsAbsent nuốt mất lệnh xoá", R1060 ·
   *   R1077 · R1087 · R1093 · R1094). Bản cũ ghi lại cả đơn mỗi lần lưu với `x || undefined` (`Az.tsx:6720-6750`), nên ô
   *   để trống/0 là XOÁ: bỏ Dịch vụ rồi lưu nháp · xoá Khối lượng · chuyển "theo hợp đồng" sang "thoả thuận" · đổi RF sang
   *   DC (hai khoản Phân bổ dầu / Khấu hao vỏ lạnh về 0 — lỗi anh báo 23/08). Trước đây `''` thành VẮNG ⇒ máy chủ giữ số cũ.
   */
  packId: blankAsAbsent(Uuid.nullable()),
  weightTon: blankAsAbsent(z.coerce.number().nullable()),
  contractPriceId: blankAsAbsent(Uuid.nullable()),
  estOffBook: blankAsAbsent(moneyField('Chi ngoài').nullable()),
  estDepreciation: blankAsAbsent(moneyField('Phân bổ khấu hao').nullable()),
})

/**
 * Cửa ĐỔI TRẠNG THÁI MỘT ĐƠN cũ (`/az/orders/:id/status`, màn vỏ lạnh dùng) — CÙNG luật với `/doi-trang-thai`
 * (C3/V3-03): ba ô tuỳ chọn dưới đây mang đúng nghĩa như `StatusFlowBody`, cho một đơn.
 */
export const OrderStatusBody = z.object({
  version: Version,
  status: OrderStatus,
  /** Huỷ / lùi về "Chờ xác nhận" mà đơn còn chặng / chi phí nháp: người dùng ĐÃ XÁC NHẬN gỡ. */
  unwind: z.boolean().optional(),
  /** "Đã giao hàng": từng chặng chưa giao — đồng bộ theo đơn, hoặc ghi LÝ DO (`LegSyncModal`). */
  legSync: z.array(z.object({ legId: Uuid, version: Version, deliver: z.boolean(), reason: z.string().optional() })).max(50).optional(),
  /** Ngày giao thực tế (mặc định theo kế hoạch). */
  actualDeliver: blankAsAbsent(ISODate),
})
export type OrderStatusBodyT = z.infer<typeof OrderStatusBody>

/* ══ ĐỔI TRẠNG THÁI ĐƠN ĐÚNG QUY TRÌNH (`setJobStatus`) — một hay nhiều đơn ═══════════════════════════════════════════ */

export const StatusFlowQuery = z.object({
  status: OrderStatus, jobIds: z.string().min(1),
  /** Lối Điều hành ĐH / Chi tiết đơn: chặn "Đã giao"/"Hủy" đơn Vỏ lạnh theo dõi (bản cũ `Az.tsx:7741-7748 · 8866`, T11). */
  reeferGuard: blankAsAbsent(BoolQuery),
})

export const StatusFlowBody = z.object({
  status: OrderStatus,
  jobs: z.array(z.object({ jobId: Uuid, version: Version })).min(1, 'Chưa chọn đơn nào.').max(ORDER_BULK_MAX, BULK_TOO_MANY),
  /** Huỷ / lùi về "Chờ xác nhận": người dùng ĐÃ XÁC NHẬN gỡ chặng + chi phí nháp. */
  unwind: z.boolean().optional(),
  /** "Đã giao hàng": từng chặng chưa giao — đồng bộ theo đơn, hoặc ghi LÝ DO vì sao chưa (`LegSyncModal`). */
  legSync: z.array(z.object({ legId: Uuid, version: Version, deliver: z.boolean(), reason: z.string().optional() })).optional(),
  /** Ngày giao thực tế của từng đơn (mặc định theo kế hoạch) — lưu cùng lúc đổi trạng thái (user 25/08). */
  actualDeliver: z.array(z.object({ jobId: Uuid, date: ISODate })).optional(),
  /** Như `StatusFlowQuery.reeferGuard` — cửa ghi kiểm LẠI cùng luật. */
  reeferGuard: z.boolean().optional(),
  /** Đổi HÀNG LOẠT từ thanh Điều hành — lịch sử đơn ghi "Đổi trạng thái → X (hàng loạt)" (bản cũ `Az.tsx:7803`, H01314). */
  opsBulk: z.boolean().optional(),
})
/** Bảng Điều hành: tóm tắt chặng · giá vốn · ngày hóa đơn cho một tập đơn (đơn ngoài phạm vi xem bị bỏ qua). */
export const OpsBoardBody = z.object({ jobIds: z.array(Uuid).max(5000) })
export type OpsBoardBodyT = z.infer<typeof OpsBoardBody>
/** 🧹 Điền bù hạn giao cho đơn thiếu — `dryRun` chỉ đếm để hỏi lại trước khi ghi. */
export const FillDueBody = z.object({
  dryRun: z.boolean(),
  /**
   * ĐÚNG các đơn thiếu hạn giao đang hiện ở màn Báo cáo vận hành (đã áp bộ lọc loại cont · khách · NCC · dịch vụ, GỒM đơn nháp) — bản
   * cũ `targets = liveJobs.filter(!dueAt)` (`Az.tsx:11378-11379`; kiểm kê 23/10 H01959 · H01997). Vắng ⇒ mọi đơn thiếu hạn giao
   * trong phạm vi (không nháp) như trước.
   */
  jobIds: z.array(Uuid).max(5000).optional(),
})
export type FillDueBodyT = z.infer<typeof FillDueBody>
/** 🔓 Nhả bắn chuyến hàng loạt — gỡ chặng + chi phí chặng còn nháp. */
export const ReleaseDispatchBody = z.object({ jobIds: z.array(Uuid).min(1, 'Chưa chọn đơn nào.').max(ORDER_BULK_MAX, BULK_TOO_MANY) })
export type ReleaseDispatchBodyT = z.infer<typeof ReleaseDispatchBody>
export type StatusFlowQueryT = z.infer<typeof StatusFlowQuery>
export type StatusFlowBodyT = z.infer<typeof StatusFlowBody>

/**
 * MỘT BẢN SAO — chép `CopyRow` (`proship-trans/src/pages/Az.tsx:6244`).
 *
 * ⭐ VÌ SAO TỪNG BẢN CÓ Ô RIÊNG. Việc thật của nghiệp vụ là *"cùng một tuyến, cùng một khách,
 * mười cái container khác nhau"*. Mỗi cont có SỐ CONT và SỐ SEAL riêng, và thường lệch nhau vài
 * ngày bốc. Chép N bản y hệt rồi bắt người dùng mở từng đơn sửa lại là mười lần mở form —
 * đúng thứ bản cũ dựng hộp thoại này để cắt.
 *
 * ⚠ Để trống ô nào thì bản sao GIỮ giá trị của đơn gốc cho ô đó, TRỪ số cont và số seal: hai thứ
 *   ấy không bao giờ được kế thừa (xem chú thích trong `copyOrder`).
 */
export const CopyOrderRow = z.object({
  contNo: z.string().optional(),
  sealNo: z.string().optional(),
  /**
   * Loại cont của bản sao. Vắng ⇒ giữ của đơn gốc; `null` ⇒ người dùng XOÁ TRẮNG ô (điền sẵn) ⇒ bản sao KHÔNG có loại cont (bản cũ
   * `contType: r.contType || undefined`, `Az.tsx:2479`; kiểm kê 23/10 R0718).
   */
  contType: z.string().nullable().optional(),
  /** Tiền CƯỚC của bản sao — ghi đè dòng doanh thu `freight`, các dòng khác giữ nguyên. */
  amount: blankAsAbsent(moneyField('Giá cước bản sao')),
  /** Ngày BỐC. */
  planDate: blankAsAbsent(ISODate),
  /** Ngày GIAO kế hoạch. */
  dueAt: blankAsAbsent(ISODate),
})

/**
 * ⚠ HAI CÁCH GỌI, CỐ Ý GIỮ CẢ HAI — chúng ứng với hai thao tác KHÁC NHAU của bản cũ:
 *   · `times` (+ `planDate`) — N bản y hệt. Dùng cho lối gọi gọn.
 *   · `rows` — mỗi bản một dòng, có ô riêng. Đây là hộp thoại mở khi bấm ĐÚP nút ⧉.
 * Có `rows` thì số bản là `rows.length` và `times` bị bỏ qua.
 */
export const CopyOrderBody = z.object({
  version: Version,
  times: z.coerce.number().int().min(1, 'Ít nhất một bản.').max(50, 'Tối đa 50 bản một lần.')
    .optional(),
  planDate: blankAsAbsent(ISODate),
  rows: z.array(CopyOrderRow).min(1, 'Ít nhất một bản.').max(50, 'Tối đa 50 bản một lần.')
    .optional(),
}).refine((b) => b.times !== undefined || b.rows !== undefined, {
  message: 'Thiếu số bản sao — gửi `times` hoặc `rows`.',
})

export const ListOrderQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  status: OrderStatus.optional(),
  ownerUserId: blankAsAbsent(Uuid),
  planFrom: blankAsAbsent(ISODate),
  planTo: blankAsAbsent(ISODate),
  includeDraft: blankAsAbsent(BoolQuery),
})

export const OrderIdParams = z.object({ id: Uuid })

export type ListOrderQueryT = z.infer<typeof ListOrderQuery>
export type CopyOrderRowT = z.infer<typeof CopyOrderRow>
export type CopyOrderBodyT = z.infer<typeof CopyOrderBody>
export type CreateOrderBodyT = z.infer<typeof CreateOrderBody>
export type UpdateOrderBodyT = z.infer<typeof UpdateOrderBody>

/* ══ TRA CỨU ĐƠN CÔNG KHAI (F20) ═════════════════════════════════════════════════════════════ */

/**
 * ⚠ HAI Ô, KHÔNG CÓ Ô THỨ BA. Đây là cửa duy nhất chạy khi không ai đăng nhập, nên mỗi tham số
 *   thêm vào là một mặt tấn công mới. Không có bộ lọc, không có phân trang, không có `fields`.
 * ⚠ `min(1)` chứ không `blankAsAbsent`: ô trống ở đây là LỖI NHẬP LIỆU phải nói ra, không phải
 *   "bỏ qua bộ lọc này" như ở các cửa danh sách.
 */
export const TrackQuery = z.object({
  tax: z.string().min(1, 'Chưa nhập mã số thuế.').max(30),
  ref: z.string().min(1, 'Chưa nhập mã đơn hàng.').max(40),
})

export type TrackQueryT = z.infer<typeof TrackQuery>

/** Xoá ô phân bổ dầu / khấu hao của đơn để đổi kỳ giá phân bổ (Giá công bố ▸ Giá phân bổ lạnh). */
export const ClearAllocBody = z.object({ version: Version, kind: z.enum(['fuel', 'depreciation']) })
export type ClearAllocBodyT = z.infer<typeof ClearAllocBody>

/* ══ ĐƠN HỆ RATRACO CŨ — NẰM CHUNG `az_job`, CHỈ XEM + ĐỔI TRẠNG THÁI (0151, anh chốt 16/10/2026) ════════════
 *
 * Ghi bằng kịch bản di trú (`scripts/migrate-ratraco.ts`) qua cửa nhập lô của admin. Kịch bản đã quy đổi khuôn hệ
 * cũ sang khuôn đơn rs-erp (trạng thái tám bước, điểm lấy/giao, dòng doanh thu) và tra sẵn id khách/sale.
 */
const OrderRef = z.string().regex(/^RS-[0-9]{8}-[0-9]{4}$/, { message: 'Mã đơn phải dạng RS-YYYYMMDD-####.' })
const Text = z.string().max(2000)
const Decimal = z.string().regex(/^-?\d{1,12}(\.\d{1,3})?$/, { message: 'Không phải số thập phân hợp lệ.' })

export const LegacyOrderRow = z.object({
  legacyId: z.string().min(1).max(64),
  ref: OrderRef,
  createdAt: ISOTimestamp,
  customerId: Uuid,
  ownerUserId: Uuid.nullable(),
  pickupAddr: Text,
  dropAddr: Text,
  pickups: z.array(z.record(z.unknown())).max(10),
  drops: z.array(z.record(z.unknown())).max(10),
  cargo: Text,
  weightTon: Decimal.nullable(),
  contNo: Text,
  contSize: z.enum(['20', '40', '45']).nullable(),
  contType: z.string().max(20).nullable(),
  packCode: z.string().max(40),
  routeName: Text,
  planDate: ISODate,
  dueAt: ISODate.nullable(),
  trainNo: Text,
  sealNo: Text,
  needInvoice: z.boolean(),
  revenues: z.array(RevenueLine).max(20),
  costPriceAt: moneyField('Tổng chi phí').nullable(),
  orderStatus: OrderStatus,
  salesNote: Text,
  note: Text,
})

/** Lô tối đa 200 dòng. */
export const ImportLegacyBody = z.object({
  rows: z.array(LegacyOrderRow).min(1).max(200),
  dryRun: z.boolean().default(false),
})

/** Đổi trạng thái đơn hệ cũ — CHỈ trạng thái, không luồng nào khác. */
export const SetLegacyStatusBody = z.object({
  status: OrderStatus,
  jobs: z.array(z.object({ jobId: Uuid, version: Version })).min(1).max(ORDER_BULK_MAX, BULK_TOO_MANY),
})

/** Mã hệ cũ đã cấp mà không chuyển (đơn từ 01/10) — rs-erp đánh số tiếp sau, không cấp lại. */
export const ReserveRefsBody = z.object({
  refs: z.array(OrderRef).min(1).max(500),
  note: z.string().max(500).default(''),
})

export type LegacyOrderRowT = z.infer<typeof LegacyOrderRow>
export type ImportLegacyBodyT = z.infer<typeof ImportLegacyBody>
export type SetLegacyStatusBodyT = z.infer<typeof SetLegacyStatusBody>
export type ReserveRefsBodyT = z.infer<typeof ReserveRefsBody>

/* ══ BẢNG ĐƠN HÀNG LỌC Ở MÁY CHỦ (đợt Đ3, `docs/KE-HOACH-DON-CU-PHAN-TRANG.md` §3.1) ══════════════════════════════ */

/** Một ô chọn nhiều — chuỗi trần như màn đang gửi (id, tên tuyến, MST, `''` cho "chưa gán"). */
const FilterList = z.array(z.string().max(200)).max(1000).default([])
const DateOrBlank = z.union([z.literal(''), ISODate]).default('')

/**
 * ⭐ ĐÚNG khuôn `OrderFilters` của `@rs/contracts/order-board.ts` — bản gốc đối chứng. Thêm ô ở đây mà không thêm ở
 *   đó (và ở SQL `@rs/order`) thì bài đối chứng đỏ. Mọi ô có mặc định ⇒ gửi `{}` là "không lọc gì".
 */
export const OrderBoardFilters = z.object({
  quick: z.enum(QUICK_KEYS).default('all'),
  // Dán cả cột mã từ Excel — vài nghìn mã vẫn lọt.
  q: z.string().max(100_000).default(''),
  qName: z.string().max(500).default(''),
  routes: FilterList,
  orderStatuses: FilterList,
  packs: FilterList,
  contTypes: FilterList,
  customers: FilterList,
  billStatuses: FilterList,
  sales: FilterList,
  trip: z.enum(['', 'yes', 'no']).default(''),
  paid: z.enum(['', 'paid', 'unpaid']).default(''),
  owe: z.enum(['', 'owe', 'clear']).default(''),
  msts: FilterList,
  partners: FilterList,
  /** Đối tác kế hoạch — id thầu phụ dự kiến; `''` = đơn chưa có. */
  plannedVendors: FilterList,
  stmts: FilterList,
  source: z.enum(ORDER_SOURCES).default(''),
  dateBasis: z.enum(DATE_BASES).default('planPickup'),
  from: DateOrBlank,
  to: DateOrBlank,
})
export type OrderBoardFiltersT = z.infer<typeof OrderBoardFilters>

/**
 * Một TRANG bảng — cửa sổ keyset hai chiều như mọi bảng đã chuyển (`limit` · `cursor` · `dir`).
 * `ids` (đợt Đ4) = đọc LẠI đúng các đơn này (≤ trần chọn hết), BỎ QUA bộ lọc, giữ phạm vi — màn đọc lại các đơn đang
 * tick trước khi mở hộp bắn chặng / xin xuất CT, và dòng đang mở chi tiết (cửa duy nhất thấy được cả đơn hệ cũ).
 */
export const OrderBoardBody = PageQuery.extend({
  filters: OrderBoardFilters.default({}),
  ids: z.array(Uuid).min(1).max(ORDER_BULK_MAX).optional(),
})
export type OrderBoardBodyT = z.infer<typeof OrderBoardBody>

/** Tóm tắt (chip · KPI · tổng số dòng) và "chọn hết" chỉ cần bộ lọc. */
export const OrderBoardFiltersBody = z.object({ filters: OrderBoardFilters.default({}) })
export type OrderBoardFiltersBodyT = z.infer<typeof OrderBoardFiltersBody>

/**
 * ⬇ Excel theo bộ lọc. `ids` = đang TICK CHỌN (≤ trần chọn hết) ⇒ chỉ xuất đúng các đơn đó, bỏ qua bộ lọc (bản cũ
 * user 23/08). Lấy theo LÔ bằng con trỏ — màn gọi lại tới khi `nextCursor = null`.
 */
export const OrderBoardExcelBody = z.object({
  filters: OrderBoardFilters.default({}),
  ids: z.array(Uuid).min(1).max(ORDER_BULK_MAX).optional(),
  cursor: z.string().min(1).optional(),
})
export type OrderBoardExcelBodyT = z.infer<typeof OrderBoardExcelBody>

/** Đơn gần nhất của MỘT khách — `OrderModal` điền sẵn. `route`/`pack` thu hẹp như `lastStops` của màn. */
export const LatestOrdersParams = z.object({ customerId: Uuid })
export const LatestOrdersQuery = z.object({
  route: z.string().max(200).optional(),
  pack: blankAsAbsent(Uuid),
  limit: z.coerce.number().int().min(1).max(50).default(20),
})
/** Tỉnh/thành đã dùng ở điểm lấy/giao (`OrderModal` gợi ý tỉnh nước ngoài) — xem `OrderProvincesDto`. */
/** ⚠ Trần RỘNG có chủ ý — tập trả về TRỘN tỉnh Việt Nam (web mới lọc bỏ), xem `usedProvincesOf`. */
export const OrderProvincesQuery = z.object({ limit: z.coerce.number().int().min(1).max(5000).default(5000) })
export type OrderProvincesQueryT = z.infer<typeof OrderProvincesQuery>
export type LatestOrdersParamsT = z.infer<typeof LatestOrdersParams>
export type LatestOrdersQueryT = z.infer<typeof LatestOrdersQuery>
